import React from "react";
import { Joyride, EventData, EVENTS, STATUS } from "react-joyride";
import type { TourDefinition } from "./types";

interface TourProps {
  tour: TourDefinition;
  enabled?: boolean;
  accountId?: string;
  primaryColor?: string;
  onStepAfter?: (index: number) => void;
  onComplete?: () => void;
  onRegisterRestart?: (restartFn: (() => void) | null) => void;
}

function hasSeen(accountId: string, tourId: string): boolean {
  try {
    return (
      JSON.parse(localStorage.getItem(`tour_seen_${accountId}`) || "{}")[
        tourId
      ] === true
    );
  } catch {
    return false;
  }
}

function markSeen(accountId: string, tourId: string): void {
  const key = `tour_seen_${accountId}`;
  try {
    const existing = JSON.parse(localStorage.getItem(key) || "{}");
    localStorage.setItem(key, JSON.stringify({ ...existing, [tourId]: true }));
  } catch {
    // ignore
  }
}

function clearSeen(accountId: string, tourId: string): void {
  const key = `tour_seen_${accountId}`;
  try {
    const existing = JSON.parse(localStorage.getItem(key) || "{}");
    delete existing[tourId];
    localStorage.setItem(key, JSON.stringify(existing));
  } catch {
    // ignore
  }
}

type Phase = "idle" | "prompt" | "running";

interface WelcomePromptProps {
  primaryColor: string;
  title: string;
  description: string;
  onAccept: () => void;
  onDecline: () => void;
}

function WelcomePrompt({
  primaryColor,
  title,
  description,
  onAccept,
  onDecline,
}: WelcomePromptProps) {
  return (
    <div
      style={{
        position: "fixed",
        top: 80,
        right: 24,
        zIndex: 9999,
        backgroundColor: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(15, 21, 38, 0.16)",
        padding: "20px 24px",
        width: 300,
        fontFamily: "Archivo, sans-serif",
      }}
    >
      <p
        style={{
          margin: "0 0 6px",
          fontWeight: 700,
          fontSize: 15,
          color: "#0F1526",
        }}
      >
        {title}
      </p>
      <p
        style={{
          margin: "0 0 20px",
          fontSize: 14,
          color: "#6F737D",
          lineHeight: 1.5,
        }}
      >
        {description}
      </p>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          onClick={onDecline}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 14,
            color: "#6F737D",
            padding: "8px 12px",
            fontFamily: "Archivo, sans-serif",
          }}
        >
          Not now
        </button>
        <button
          onClick={onAccept}
          style={{
            backgroundColor: primaryColor,
            color: "#ffffff",
            border: "none",
            borderRadius: 8,
            padding: "8px 20px",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "Archivo, sans-serif",
          }}
        >
          Show me →
        </button>
      </div>
    </div>
  );
}

function Tour({
  tour,
  enabled = true,
  accountId,
  primaryColor = "#000000",
  onStepAfter,
  onComplete,
  onRegisterRestart,
}: TourProps) {
  const [phase, setPhase] = React.useState<Phase>("idle");
  const handledRef = React.useRef(false);
  const hadMissingTargetRef = React.useRef(false);
  const accountIdRef = React.useRef(accountId);
  const tourIdRef = React.useRef(tour.id);
  accountIdRef.current = accountId;
  tourIdRef.current = tour.id;

  React.useEffect(() => {
    if (!enabled || !accountId || hasSeen(accountId, tour.id)) return;
    const timer = setTimeout(() => setPhase("prompt"), 300);
    return () => clearTimeout(timer);
  }, [enabled, accountId, tour.id]);

  const restart = React.useCallback(() => {
    const aid = accountIdRef.current;
    const tid = tourIdRef.current;
    if (!aid) return;
    clearSeen(aid, tid);
    handledRef.current = false;
    hadMissingTargetRef.current = false;
    setPhase("running");
  }, []);

  React.useEffect(() => {
    onRegisterRestart?.(restart);
    return () => onRegisterRestart?.(null);
    // restart is stable; onRegisterRestart comes from context (stable ref)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = React.useCallback(
    (data: EventData) => {
      const { type, index, status } = data;
      if (type === EVENTS.STEP_AFTER) {
        onStepAfter?.(index);
      }
      // Track missing targets so we don't permanently suppress the tour
      // for a user who never actually saw a broken step.
      if (type === EVENTS.TARGET_NOT_FOUND) {
        hadMissingTargetRef.current = true;
      }
      if (type === EVENTS.TOUR_END && !handledRef.current) {
        handledRef.current = true;
        setPhase("idle");
        if (accountId && !hadMissingTargetRef.current) {
          markSeen(accountId, tour.id);
        }
        hadMissingTargetRef.current = false;
        if (status === STATUS.FINISHED) {
          onComplete?.();
        }
      }
    },
    [accountId, tour.id, onStepAfter, onComplete]
  );

  return (
    <>
      {phase === "prompt" && (
        <WelcomePrompt
          primaryColor={primaryColor}
          title={tour.promptTitle ?? "Take a quick tour?"}
          description={
            tour.promptDescription ??
            "Learn the key features in under 2 minutes."
          }
          onAccept={() => {
            handledRef.current = false;
            setPhase("running");
          }}
          onDecline={() => {
            if (accountId) markSeen(accountId, tour.id);
            setPhase("idle");
          }}
        />
      )}
      <Joyride
        steps={tour.steps}
        run={phase === "running"}
        continuous
        onEvent={handleCallback}
        locale={{ last: "Got it", skip: "Skip" }}
        options={{
          primaryColor,
          backgroundColor: "#ffffff",
          textColor: "#0F1526",
          overlayColor: "rgba(15, 21, 38, 0.5)",
          zIndex: 10000,
          overlayClickAction: false,
          showProgress: true,
          scrollOffset: 120,
        }}
        styles={{
          tooltip: {
            fontFamily: "Archivo, sans-serif",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(15, 21, 38, 0.12)",
            padding: "20px 24px",
          },
          tooltipTitle: {
            fontFamily: "Archivo, sans-serif",
            fontWeight: 700,
            fontSize: 15,
            color: "#0F1526",
            marginBottom: 8,
          },
          tooltipContent: {
            fontFamily: "Archivo, sans-serif",
            fontSize: 14,
            color: "#6F737D",
            padding: "4px 0 0",
          },
          tooltipFooter: { marginTop: 16 },
          buttonPrimary: {
            fontFamily: "Archivo, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 8,
            padding: "8px 20px",
            backgroundColor: primaryColor,
          },
          buttonBack: {
            fontFamily: "Archivo, sans-serif",
            fontWeight: 500,
            fontSize: 14,
            color: "#3F4451",
            marginRight: 8,
          },
          buttonSkip: {
            fontFamily: "Archivo, sans-serif",
            fontSize: 13,
            color: "#9FA1A8",
          },
          buttonClose: { color: "#9FA1A8" },
        }}
      />
    </>
  );
}

export default Tour;
